import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Film,
  Play,
  Star,
  Clock,
  Calendar,
  Search,
  ChevronLeft,
  X,
  Layers,
  Sparkles,
  Zap,
  LayoutGrid,
  List as ListIcon,
  SlidersHorizontal,
  Check,
  RotateCcw,
  History,
  CheckCircle2,
  Menu,
  Trash2,
  Info,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { Movie } from '../../types/iptv';
import { RatingsBadges } from '../common/RatingsBadges';
import { AutoScrollTitle } from '../common/AutoScrollTitle';
import { OpenTvLoader } from '../common/OpenTvLoader';
import { getSafeImageUrl, handleMovieImageError } from '../../utils/imageUtils';
import { extractReleaseYear } from '../../utils/m3uParser';
import { shouldHideCategoryFromList, shouldHideContentFromList } from '../../utils/security';
import { fetchTmdbMovieInfo, TmdbMovieMetadata } from '../../utils/tmdb';
import { EmptyCategoryGuide } from '../common/EmptyCategoryGuide';

const getDisplayYear = (title: string, year?: number) => {
  if (year && year >= 1920 && year <= 2025) return year;
  const extracted = extractReleaseYear(title);
  if (extracted >= 1920 && extracted <= 2026) return extracted;
  return year && year >= 1920 && year <= 2026 ? year : undefined;
};

export const MoviesView: React.FC = () => {
  const {
    movies,
    movieCategories,
    rawMovieCategories,
    categoryConfig,
    activeMovieCategory,
    setActiveMovieCategory,
    setCurrentMovie,
    isFavorite,
    toggleFavorite,
    isCategoryFavorite,
    toggleCategoryFavorite,
    currentProfile,
    parentalConfig,
    getContentProgress,
    clearContentProgress,
    clearAllWatchHistory,
    isContentWatched,
    toggleWatched,
  } = useIPTV();

  // Estado independente para filtrar categorias na barra lateral/gaveta
  const [categoryFilter, setCategoryFilter] = useState('');
  // Estado independente para buscar filmes na grade
  const [movieSearch, setMovieSearch] = useState('');
  const [selectedDetailMovie, setSelectedDetailMovie] = useState<Movie | null>(null);
  const [tmdbDetails, setTmdbDetails] = useState<TmdbMovieMetadata | null>(null);
  const [isFetchingTmdb, setIsFetchingTmdb] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [visibleLimit, setVisibleLimit] = useState(60);
  const [isMobileCategoryDrawerOpen, setIsMobileCategoryDrawerOpen] = useState(false);
  const contentAreaRef = useRef<HTMLDivElement>(null);

  // Enriquecimento automático de sinopse e detalhes via TMDB se a lista IPTV vier sem sinopse
  useEffect(() => {
    if (!selectedDetailMovie) {
      setTmdbDetails(null);
      setIsFetchingTmdb(false);
      return;
    }

    const hasShortOrNoDesc = !selectedDetailMovie.description || selectedDetailMovie.description.trim().length < 25;
    if (hasShortOrNoDesc) {
      setIsFetchingTmdb(true);
      const parsedYear = getDisplayYear(selectedDetailMovie.title, selectedDetailMovie.year);
      fetchTmdbMovieInfo(selectedDetailMovie.title, parsedYear)
        .then((meta) => {
          if (meta) {
            setTmdbDetails(meta);
          }
        })
        .finally(() => {
          setIsFetchingTmdb(false);
        });
    } else {
      setTmdbDetails(null);
      setIsFetchingTmdb(false);
    }
  }, [selectedDetailMovie]);

  const continueWatchingMoviesCount = useMemo(() => {
    if (!currentProfile.watchHistory || currentProfile.watchHistory.length === 0) return 0;
    return movies.filter((m) => {
      if (shouldHideCategoryFromList(m.category, currentProfile, parentalConfig)) return false;
      if (shouldHideContentFromList(m, currentProfile, parentalConfig)) return false;
      if (isContentWatched(m.id)) return false; // assistido sai do "Continuar Assistindo"
      return currentProfile.watchHistory?.some((h) => h.contentId === m.id);
    }).length;
  }, [movies, currentProfile, parentalConfig]);

  // Categorias para a barra lateral seguindo RIGOROSAMENTE a ordem definida no Gerenciador / Banco:
  // 1º Continuar Assistindo (se houver)
  // 2º Favoritos
  // 3º Categorias marcadas como favoritas com a Estrela ⭐ (no topo, logo abaixo de Favoritos)
  // 4º Demais Categorias na sequência EXATA configurada no Gerenciador / Banco
  // E ao desfavoritar, a categoria retorna automaticamente à sua posição original do banco!
  const filteredCategoryCards = useMemo(() => {
    // 1. Filtrar categorias ocultas por perfil / parental
    const visible = movieCategories.filter(
      (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
    );

    const specialPrefix: string[] = [];
    const regularCats: string[] = [];

    visible.forEach((cat) => {
      if (cat === 'Continuar Assistindo' || cat === 'Favoritos') {
        specialPrefix.push(cat);
      } else {
        regularCats.push(cat);
      }
    });

    // Separar categorias normais entre favoritadas com ⭐ e não favoritadas
    const starredCats: string[] = [];
    const unstarredCats: string[] = [];

    regularCats.forEach((cat) => {
      if (isCategoryFavorite('movies', cat)) {
        starredCats.push(cat);
      } else {
        unstarredCats.push(cat);
      }
    });

    // Se houver filtro digitado pelo usuário, filtrar as categorias também
    let fullList = [...specialPrefix, ...starredCats, ...unstarredCats];
    if (categoryFilter.trim()) {
      const q = categoryFilter.toLowerCase().trim();
      fullList = fullList.filter(
        (cat) => cat === 'Continuar Assistindo' || cat === 'Favoritos' || cat.toLowerCase().includes(q)
      );
    }
    return fullList;
  }, [movieCategories, currentProfile, parentalConfig, isCategoryFavorite, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of filteredCategoryCards) {
      if (cat === 'Continuar Assistindo') {
        counts[cat] = continueWatchingMoviesCount;
      } else if (cat === 'Favoritos') {
        counts[cat] = movies.filter((m) => isFavorite(m.id)).length;
      } else {
        const rename = categoryConfig.movies.renames[cat];
        counts[cat] = movies.filter((m) => m.category === cat || rename === cat).length;
      }
    }
    return counts;
  }, [filteredCategoryCards, movies, isFavorite, continueWatchingMoviesCount, categoryConfig.movies.renames]);


  // Filtered movies inside active category or global search across all categories
  const categoryMovies = useMemo(() => {
    const isSearching = !!movieSearch.trim();
    const moviesHidden = new Set(categoryConfig?.movies?.hidden || []);
    const movieRenames = categoryConfig?.movies?.renames || {};

    let list = movies.filter((m) => {
      if (shouldHideContentFromList(m, currentProfile, parentalConfig)) return false;

      // Ocultar filmes cujas categorias estejam ocultas no Gerenciador ou por Controle Parental
      const mCat = m.category || 'Geral';
      const rename = movieRenames[mCat];
      if (moviesHidden.has(mCat) || (rename && moviesHidden.has(rename))) return false;
      if (shouldHideCategoryFromList(mCat, currentProfile, parentalConfig)) return false;
      if (rename && shouldHideCategoryFromList(rename, currentProfile, parentalConfig)) return false;

      if (isSearching) {
        const q = movieSearch.toLowerCase().trim();
        const matchTitle = m.title.toLowerCase().includes(q);
        const matchCat = (m.category || '').toLowerCase().includes(q) || (rename && rename.toLowerCase().includes(q));
        const matchDesc = m.description ? m.description.toLowerCase().includes(q) : false;
        const matchYear = m.year ? m.year.toString().includes(q) : false;
        return matchTitle || matchCat || matchDesc || matchYear;
      }

      if (activeMovieCategory === 'Favoritos') {
        if (!isFavorite(m.id)) return false;
      } else if (activeMovieCategory === 'Continuar Assistindo') {
        const inHistory = currentProfile.watchHistory?.some((h) => h.contentId === m.id);
        if (!inHistory) return false;
        if (isContentWatched(m.id)) return false; // filme assistido sai do "Continuar Assistindo"
      } else if (activeMovieCategory) {
        const rename = categoryConfig.movies.renames[m.category];
        if (m.category !== activeMovieCategory && rename !== activeMovieCategory) {
          return false;
        }
      }

      return true;
    });

    if (activeMovieCategory === 'Continuar Assistindo' && !isSearching) {
      list = [...list].sort((a, b) => {
        const histA = currentProfile.watchHistory?.find((h) => h.contentId === a.id);
        const histB = currentProfile.watchHistory?.find((h) => h.contentId === b.id);
        const timeA = histA ? new Date(histA.updatedAt || 0).getTime() : 0;
        const timeB = histB ? new Date(histB.updatedAt || 0).getTime() : 0;
        return timeB - timeA;
      });
    } else {
      list = [...list].sort((a, b) => {
        // 1. Year (descending)
        const yearA = getDisplayYear(a.title, a.year) || 0;
        const yearB = getDisplayYear(b.title, b.year) || 0;
        if (yearB !== yearA) return yearB - yearA;

        // 2. Score / Rating (descending)
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        if (scoreB !== scoreA) return scoreB - scoreA;

        // 3. Assistido ou não (não assistidos primeiro)
        const isWatchedA = isContentWatched(a.id) ? 1 : 0;
        const isWatchedB = isContentWatched(b.id) ? 1 : 0;
        if (isWatchedA !== isWatchedB) return isWatchedA - isWatchedB;

        // 4. Title (A-Z)
        return a.title.localeCompare(b.title);
      });
    }

    return list;
  }, [
    movies,
    activeMovieCategory,
    categoryConfig.movies,
    movieSearch,
    isFavorite,
    isContentWatched,
    currentProfile.watchHistory,
    currentProfile,
    parentalConfig,
  ]);

  // Reset pagination limit, scroll to top when category or movieSearch changes
  React.useEffect(() => {
    setVisibleLimit(60);
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }
  }, [activeMovieCategory, movieSearch]);

  // Autofocus first movie ONLY when switching category via remote/keyboard (never while typing in an input)
  React.useEffect(() => {
    const isTyping = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';
    if (isTyping) return;

    const timer = setTimeout(() => {
      if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        const firstCard = contentAreaRef.current?.querySelector<HTMLElement>('[data-movie-card="true"], [tabindex="0"]');
        firstCard?.focus();
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [activeMovieCategory]);

  const displayedMovies = useMemo(() => {
    return categoryMovies.slice(0, visibleLimit);
  }, [categoryMovies, visibleLimit]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 450) {
      if (displayedMovies.length < categoryMovies.length) {
        setVisibleLimit((prev) => prev + 60);
      }
    }
  };

  // If a movie detail is open
  if (selectedDetailMovie) {
    const movieProgress = getContentProgress(selectedDetailMovie.id);
    const hasProgress = movieProgress && movieProgress.watchedSeconds > 10 && !movieProgress.isCompleted;

    return (
      <div className="w-full h-full flex flex-col bg-zinc-950 overflow-y-auto p-3.5 sm:p-6 md:p-8 pb-36 md:pb-16 touch-pan-y overscroll-contain select-none scrollbar-thin scrollbar-thumb-zinc-800">
        <div className="flex items-center gap-3 mb-4 sm:mb-6 shrink-0">
          <button
            onClick={() => setSelectedDetailMovie(null)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 text-xs font-bold transition cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Voltar para {activeMovieCategory || 'Filmes'}</span>
          </button>
        </div>

        <div className="relative rounded-2xl md:rounded-3xl bg-zinc-900 border border-zinc-800 p-4 sm:p-6 md:p-8 flex flex-col md:flex-row gap-5 md:gap-8 shadow-2xl mb-8 min-h-[300px] overflow-hidden shrink-0">
          <div className="absolute inset-0 z-0 opacity-15 overflow-hidden pointer-events-none">
            <img
              src={getSafeImageUrl(selectedDetailMovie.backdrop || selectedDetailMovie.poster)}
              alt=""
              className="w-full h-full object-cover blur-3xl scale-125"
            />
          </div>

          {/* Left Column: ONLY the True Proportion Poster */}
          <div className="w-36 sm:w-48 md:w-52 shrink-0 mx-auto md:mx-0 z-10 flex items-center justify-center">
            <img
              src={getSafeImageUrl(selectedDetailMovie.poster)}
              alt={selectedDetailMovie.title}
              className="w-full h-auto max-h-[300px] md:max-h-[340px] object-contain rounded-2xl shadow-2xl border border-zinc-800/80 bg-black/60"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500';
              }}
            />
          </div>

          {/* Right Column: Movie Details, Synopsis, & Action Buttons */}
          <div className="flex-1 flex flex-col justify-start gap-4 relative z-10 min-w-0">
            <div>
              <div className="flex flex-wrap items-center gap-2.5 mb-3">
                {getDisplayYear(selectedDetailMovie.title, selectedDetailMovie.year) && (
                  <span className="px-3 py-1 rounded-xl bg-theme-accent text-black font-black text-xs">
                    {getDisplayYear(selectedDetailMovie.title, selectedDetailMovie.year)}
                  </span>
                )}
                <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  {selectedDetailMovie.score.toFixed(1)}
                </span>
                <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs">
                  {selectedDetailMovie.category}
                </span>
                {selectedDetailMovie.duration && (
                  <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3 text-theme-accent" />
                    {selectedDetailMovie.duration} min
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-black text-white mb-3">
                {selectedDetailMovie.title}
              </h1>

              <div className="mb-4">
                <RatingsBadges score={tmdbDetails?.rating || selectedDetailMovie.score} size="md" />
              </div>

              <div className="relative">
                <p className="text-sm md:text-base text-zinc-300 leading-relaxed">
                  {tmdbDetails?.synopsis ||
                    selectedDetailMovie.description ||
                    (isFetchingTmdb ? 'Buscando sinopse completa...' : 'Nenhuma sinopse disponível para este filme no momento.')}
                </p>
                {tmdbDetails?.synopsis && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-400">
                    <Info className="w-3.5 h-3.5 text-theme-accent" />
                    <span>Sinopse enriquecida via TMDB</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons Section - Single Responsive Row */}
            <div className="pt-3 border-t border-zinc-800/80 mt-auto w-full">
              <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 w-full">
                {/* 1. Botão Principal: Continuar / Assistir Filme */}
                {movieProgress && movieProgress.watchedSeconds > 10 && !movieProgress.isCompleted ? (
                  <>
                    <button
                      onClick={() => {
                        setCurrentMovie(selectedDetailMovie, false);
                        setSelectedDetailMovie(null);
                      }}
                      className="relative overflow-hidden flex-1 min-w-[150px] px-3.5 py-2.5 rounded-xl bg-theme-accent hover:opacity-95 text-black font-black text-xs sm:text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-theme-accent-sm transition cursor-pointer"
                    >
                      <Play className="w-4 h-4 fill-current shrink-0" />
                      <span className="truncate">Continuar ({movieProgress.formattedProgress})</span>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/25">
                        <div
                          className="h-full bg-black/75 transition-all duration-300"
                          style={{ width: `${movieProgress.percentage}%` }}
                        />
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setCurrentMovie(selectedDetailMovie, true);
                        setSelectedDetailMovie(null);
                      }}
                      className="px-3 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 border border-zinc-700 shrink-0 transition cursor-pointer"
                      title="Assistir o filme do minuto 0:00"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-theme-accent" />
                      <span className="whitespace-nowrap hidden lg:inline">Do Início</span>
                    </button>

                    <button
                      onClick={() => clearContentProgress(selectedDetailMovie.id, selectedDetailMovie.title)}
                      className="p-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-red-400 border border-zinc-800 shrink-0 transition cursor-pointer"
                      title="Limpar histórico de exibição deste filme"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setCurrentMovie(selectedDetailMovie, true);
                      setSelectedDetailMovie(null);
                    }}
                    className="flex-1 min-w-[150px] px-4 py-2.5 rounded-xl bg-theme-accent text-black font-black text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-theme-accent-sm transition cursor-pointer"
                  >
                    <Play className="w-4 h-4 fill-current shrink-0" />
                    <span className="truncate">Assistir Filme</span>
                  </button>
                )}

                {/* 2. Botão: Marcar Filme como Assistido */}
                <button
                  onClick={() => toggleWatched(selectedDetailMovie.id, false)}
                  className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                    isContentWatched(selectedDetailMovie.id)
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30'
                      : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800'
                  }`}
                  title={isContentWatched(selectedDetailMovie.id) ? 'Marcar como não assistido' : 'Marcar filme como assistido'}
                >
                  <CheckCircle2 className={`w-4 h-4 shrink-0 ${isContentWatched(selectedDetailMovie.id) ? 'text-emerald-400' : 'text-zinc-400'}`} />
                  <span className="truncate">{isContentWatched(selectedDetailMovie.id) ? 'Já Assistido' : 'Marcar Visto'}</span>
                </button>

                {/* 3. Botão: Favorito */}
                <button
                  onClick={() => toggleFavorite(selectedDetailMovie.id)}
                  className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                    isFavorite(selectedDetailMovie.id)
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800'
                  }`}
                >
                  <Star
                    className={`w-4 h-4 shrink-0 ${
                      isFavorite(selectedDetailMovie.id) ? 'fill-current text-amber-400' : 'text-zinc-400'
                    }`}
                  />
                  <span className="truncate">
                    {isFavorite(selectedDetailMovie.id) ? 'Favorito' : 'Favoritar'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex bg-zinc-950 overflow-hidden select-none">
      {/* LEFT SIDEBAR: Desktop / Tablet */}
      <div className="hidden md:flex md:w-72 lg:w-80 shrink-0 bg-zinc-950 border-r border-zinc-800/80 flex-col h-full overflow-hidden">
        {/* Sidebar Header & Search */}
        <div className="p-4 border-b border-zinc-800/80 shrink-0 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs md:text-sm font-black text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-theme-accent" />
              <span>Categorias de Filmes ({movieCategories.length})</span>
            </h2>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Filtrar categorias..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {categoryFilter && (
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar filtro de categorias"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Categories Vertical List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
          {filteredCategoryCards.map((category) => {
            const isSelected = activeMovieCategory === category;
            const isFav = category === 'Favoritos';
            const isContinue = category === 'Continuar Assistindo';
            const isSpecial = isFav || isContinue;
            const isCategoryFav = !isSpecial && isCategoryFavorite('movies', category);

            const count = isContinue
              ? continueWatchingMoviesCount
              : isFav
              ? movies.filter((m) => isFavorite(m.id)).length
              : movies.filter((m) => {
                  const rename = categoryConfig.movies.renames[m.category];
                  return m.category === category || rename === category;
                }).length;

            return (
              <div
                key={category}
                className={`w-full group/cat relative flex items-center rounded-xl transition cursor-pointer ${
                  isSelected
                    ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                    : isCategoryFav
                    ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                    : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                }`}
              >
                <button
                  onClick={() => {
                    setActiveMovieCategory(category);
                    setMovieSearch('');
                    setCategoryFilter('');
                  }}
                  className="flex-1 text-left px-3 py-2.5 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {isFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'
                        }`}
                      />
                    ) : isContinue ? (
                      <History
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-black' : 'text-amber-400'
                        }`}
                      />
                    ) : isCategoryFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 fill-amber-400 ${
                          isSelected ? 'text-black fill-black' : 'text-amber-400'
                        }`}
                      />
                    ) : (
                      <Film
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-black' : 'text-zinc-500 group-hover/cat:text-theme-accent'
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <AutoScrollTitle
                        text={category}
                        className={isSelected ? 'text-black font-black' : ''}
                      />
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${
                      isSelected
                        ? 'bg-black/20 text-black font-black'
                        : isCategoryFav
                        ? 'bg-amber-500/20 text-amber-300 font-bold'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>

                {/* Star / Favorite Toggle Button for Category */}
                {!isSpecial && (
                  <button
                    type="button"
                    title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategoryFavorite('movies', category);
                    }}
                    className={`pr-2.5 pl-1 py-2 transition cursor-pointer ${
                      isSelected
                        ? 'text-black hover:opacity-75'
                        : isCategoryFav
                        ? 'text-amber-400 opacity-100 hover:scale-125'
                        : 'text-zinc-500 opacity-0 group-hover/cat:opacity-100 hover:text-amber-400 hover:scale-125'
                    }`}
                  >
                    <Star
                      className={`w-3.5 h-3.5 ${
                        isCategoryFav
                          ? isSelected
                            ? 'fill-black'
                            : 'fill-amber-400'
                          : ''
                      }`}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER/RIGHT PANEL: Movies in Card or List view */}
      <div className="flex-1 flex flex-col bg-zinc-950 h-full overflow-hidden">
        {/* Mobile Header with 3 pausinhos (Menu) to open vertical categories */}
        <div className="md:hidden p-3 border-b border-zinc-800/80 bg-zinc-900/70 flex items-center justify-between gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setIsMobileCategoryDrawerOpen(true)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-xs cursor-pointer shrink-0 active:scale-95 transition bg-theme-accent-subtle border border-theme-accent-border text-theme-accent-light shadow-theme-accent-sm ring-1 ring-theme-accent hover:opacity-90 ${
              displayedMovies.length === 0 ? 'animate-pulse' : ''
            }`}
            title="Escolha a categoria"
          >
            <Menu className="w-4 h-4 text-theme-accent" />
            <span>Escolha a categoria</span>
          </button>

          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Pesquisar filme..."
              value={movieSearch}
              onChange={(e) => {
                setMovieSearch(e.target.value);
                setIsMobileCategoryDrawerOpen(false);
              }}
              className="w-full pl-9 pr-8 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {movieSearch && (
              <button
                type="button"
                onClick={() => setMovieSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar busca de filmes"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Vertical Drawer (Menu vertical um abaixo do outro) */}
        {isMobileCategoryDrawerOpen && (
          <div className="fixed inset-0 z-50 flex bg-black/80 backdrop-blur-sm md:hidden animate-in fade-in duration-200">
            <div className="w-80 max-w-[85vw] bg-zinc-950 border-r border-zinc-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
              {/* Drawer Header */}
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/60">
                <h3 className="text-sm font-black text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-theme-accent" />
                  <span>Categorias ({movieCategories.length})</span>
                </h3>
                <button
                  onClick={() => setIsMobileCategoryDrawerOpen(false)}
                  className="p-1.5 rounded-xl text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Search (Filtro exclusivo de categorias) */}
              <div className="p-3 border-b border-zinc-800 bg-zinc-900/40">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Filtrar categorias..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
                  />
                  {categoryFilter && (
                    <button
                      type="button"
                      onClick={() => setCategoryFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                      title="Limpar filtro"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Vertical List one below the other */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
                {filteredCategoryCards.map((category) => {
                  const isSelected = activeMovieCategory === category;
                  const isFav = category === 'Favoritos';
                  const isContinue = category === 'Continuar Assistindo';
                  const isSpecial = isFav || isContinue;
                  const isCategoryFav = !isSpecial && isCategoryFavorite('movies', category);

                  const count = isContinue
                    ? continueWatchingMoviesCount
                    : isFav
                    ? movies.filter((m) => isFavorite(m.id)).length
                    : movies.filter((m) => {
                        const rename = categoryConfig.movies.renames[m.category];
                        return m.category === category || rename === category;
                      }).length;

                  return (
                    <div
                      key={category}
                      className={`w-full flex items-center rounded-xl transition cursor-pointer ${
                        isSelected
                          ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                          : isCategoryFav
                          ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                          : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                      }`}
                    >
                      <button
                        onClick={() => {
                          setActiveMovieCategory(category);
                          setMovieSearch('');
                          setCategoryFilter('');
                          setIsMobileCategoryDrawerOpen(false);
                        }}
                        className="flex-1 text-left px-3.5 py-3 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          {isFav ? (
                            <Star className={`w-4 h-4 shrink-0 ${isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'}`} />
                          ) : isContinue ? (
                            <History className={`w-4 h-4 shrink-0 ${isSelected ? 'text-black' : 'text-amber-400'}`} />
                          ) : isCategoryFav ? (
                            <Star className={`w-4 h-4 shrink-0 fill-amber-400 ${isSelected ? 'text-black fill-black' : 'text-amber-400'}`} />
                          ) : (
                            <Film className={`w-4 h-4 shrink-0 ${isSelected ? 'text-black' : 'text-zinc-500'}`} />
                          )}
                          <span className="truncate">{category}</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${isSelected ? 'bg-black/20 text-black font-black' : 'bg-zinc-800 text-zinc-400'}`}>
                          {count}
                        </span>
                      </button>

                      {/* Botão da Estrela no Drawer Mobile */}
                      {!isSpecial && (
                        <button
                          type="button"
                          title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryFavorite('movies', category);
                          }}
                          className={`pr-3 pl-1 py-3 transition cursor-pointer shrink-0 ${
                            isSelected
                              ? 'text-black hover:opacity-75'
                              : isCategoryFav
                              ? 'text-amber-400 opacity-100 hover:scale-125'
                              : 'text-zinc-500 opacity-60 hover:opacity-100 hover:text-amber-400 hover:scale-125'
                          }`}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isCategoryFav
                                ? isSelected
                                  ? 'fill-black'
                                  : 'fill-amber-400'
                                : ''
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex-1 cursor-pointer" onClick={() => setIsMobileCategoryDrawerOpen(false)} />
          </div>
        )}

        {/* Top Control Bar */}
        <div className="p-3 sm:p-4 md:p-6 border-b border-zinc-800/80 bg-zinc-900/40 flex items-center justify-between gap-2.5 shrink-0">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <h1 className="text-sm sm:text-base md:text-xl font-black text-white truncate">
              {movieSearch.trim()
                ? `Busca: "${movieSearch.trim()}" (${categoryMovies.length} ${categoryMovies.length === 1 ? 'filme' : 'filmes'})`
                : (activeMovieCategory || 'Favoritos')}
            </h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Campo de Busca de Filmes no Desktop (Top Bar) */}
            <div className="hidden md:flex relative w-44 lg:w-60">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Pesquisar filme..."
                value={movieSearch}
                onChange={(e) => setMovieSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent transition"
              />
              {movieSearch && (
                <button
                  type="button"
                  onClick={() => setMovieSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                  title="Limpar busca de filmes"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            {/* Clear all Continue Watching for Movies */}
            {activeMovieCategory === 'Continuar Assistindo' && displayedMovies.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Deseja limpar todo o histórico de Continuar Assistindo de filmes deste perfil?')) {
                    clearAllWatchHistory();
                  }
                }}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-red-950 border border-zinc-800 hover:border-red-500/40 text-xs font-bold text-zinc-400 hover:text-red-400 transition cursor-pointer"
                title="Limpar todos os filmes de Continuar Assistindo"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Limpar Histórico</span>
              </button>
            )}

            {/* Toggle Favorite on Active Category */}
            {activeMovieCategory && activeMovieCategory !== 'Favoritos' && activeMovieCategory !== 'Continuar Assistindo' && (
              <button
                onClick={() => toggleCategoryFavorite('movies', activeMovieCategory)}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl border text-xs font-bold transition cursor-pointer ${
                  isCategoryFavorite('movies', activeMovieCategory)
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 shadow-sm'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
                }`}
                title={isCategoryFavorite('movies', activeMovieCategory) ? 'Remover dos favoritos' : 'Favoritar Categoria'}
              >
                <Star
                  className={`w-3.5 h-3.5 shrink-0 ${
                    isCategoryFavorite('movies', activeMovieCategory)
                      ? 'fill-amber-400 text-amber-400'
                      : ''
                  }`}
                />
                <span className="hidden sm:inline">
                  {isCategoryFavorite('movies', activeMovieCategory)
                    ? 'Favorita'
                    : 'Favoritar Categoria'}
                </span>
              </button>
            )}

            {/* View Mode Toggle (Cards vs List) */}
            <div className="flex items-center bg-zinc-900 p-0.5 rounded-xl border border-zinc-800 shrink-0">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'cards'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Lista"
              >
                <ListIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Movies Content Area */}
        <div
          ref={contentAreaRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin scrollbar-thumb-zinc-800"
        >
          {displayedMovies.length === 0 ? (
            movies.length === 0 ? (
              <div className="py-20 text-center max-w-md mx-auto px-4">
                <Film className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">
                  Nenhum Filme Cadastrado
                </h3>
                <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
                  Conecte sua lista IPTV nas configurações para carregar o catálogo VOD de filmes.
                </p>
              </div>
            ) : (
              <EmptyCategoryGuide
                type="movie"
                categoryName={activeMovieCategory || 'Continuar Assistindo'}
                availableCategories={filteredCategoryCards}
                onSelectCategory={(cat) => {
                  setActiveMovieCategory(cat);
                  setMovieSearch('');
                  setCategoryFilter('');
                }}
                onOpenMobileDrawer={() => setIsMobileCategoryDrawerOpen(true)}
                categoryCounts={categoryCounts}
                totalItemsCount={movies.length}
              />
            )
          ) : viewMode === 'cards' ? (
            /* CARDS GRID VIEW */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3.5">
              {displayedMovies.map((movie) => {
                const fav = isFavorite(movie.id);
                const isWatchedMovie = isContentWatched(movie.id);
                const prog = getContentProgress(movie.id);
                const hasWatched = prog && prog.watchedSeconds > 10 && !prog.isCompleted;

                return (
                  <div
                    key={movie.id}
                    data-movie-card="true"
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      setSelectedDetailMovie(movie);
                      setMovieSearch('');
                      setCategoryFilter('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setSelectedDetailMovie(movie);
                        setMovieSearch('');
                        setCategoryFilter('');
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(movie.id);
                      }
                    }}
                    className={`group relative p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800/90 hover:border-theme-accent/50 hover:bg-zinc-800/90 transition-all duration-200 cursor-pointer flex flex-col justify-between select-none shadow-lg focus:outline-none focus:ring-2 focus:ring-theme-accent virtual-card-contain ${
                      isWatchedMovie ? 'opacity-45 hover:opacity-100' : ''
                    }`}
                  >
                    <div className="relative w-full aspect-[2/3] rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden mb-2.5">
                      <div className="absolute inset-0 skeleton-shimmer z-0" />
                      <img
                        src={getSafeImageUrl(movie.poster)}
                        alt={movie.title}
                        className="relative z-10 w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => handleMovieImageError(e, movie.title, movie.category, movie.year)}
                      />

                      {/* Watched Badge */}
                      {isWatchedMovie && (
                        <div className="absolute top-2 left-2 z-10">
                          <span className="px-2 py-0.5 rounded-lg bg-emerald-950/90 backdrop-blur-md border border-emerald-500/40 text-emerald-400 text-[10px] font-black flex items-center gap-1 shadow">
                            <CheckCircle2 className="w-3 h-3" /> Assistido
                          </span>
                        </div>
                      )}

                      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                        {activeMovieCategory === 'Continuar Assistindo' && (
                          <div
                            aria-hidden="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearContentProgress(movie.id, movie.title);
                            }}
                            className="p-1.5 rounded-xl bg-black/70 backdrop-blur-md text-zinc-400 hover:text-red-400 hover:bg-red-950/80 transition shadow cursor-pointer"
                            title="Remover de Continuar Assistindo"
                          >
                            <X className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <div
                          aria-hidden="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(movie.id);
                          }}
                          className="p-1.5 rounded-xl bg-black/70 backdrop-blur-md text-zinc-300 hover:text-amber-400 transition shadow cursor-pointer"
                        >
                          <Star
                            className={`w-3.5 h-3.5 ${
                              fav ? 'fill-amber-400 text-amber-400' : ''
                            }`}
                          />
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1">
                        {getDisplayYear(movie.title, movie.year) && (
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-black/80 text-theme-accent-light border border-theme-accent-border">
                            {getDisplayYear(movie.title, movie.year)}
                          </span>
                        )}
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-black flex items-center gap-0.5">
                          <Star className="w-2.5 h-2.5 fill-current" />
                          {movie.score.toFixed(1)}
                        </span>
                        {hasWatched && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-theme-accent text-black">
                            {prog.percentage}%
                          </span>
                        )}
                      </div>
                      {hasWatched && (
                        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/80">
                          <div
                            className="h-full bg-theme-accent"
                            style={{ width: `${prog.percentage}%` }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <AutoScrollTitle
                        text={movie.title}
                        className="text-xs font-bold text-white group-hover:text-theme-accent-light transition"
                      />
                      <p className="text-[10px] text-zinc-400 truncate">
                        {movie.category}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* LIST VIEW (TABLE) */
            <div className="space-y-1.5">
              {displayedMovies.map((movie) => {
                const fav = isFavorite(movie.id);
                const isWatchedMovie = isContentWatched(movie.id);
                const prog = getContentProgress(movie.id);
                const hasWatched = prog && prog.watchedSeconds > 10 && !prog.isCompleted;

                return (
                  <div
                    key={movie.id}
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      setSelectedDetailMovie(movie);
                      setMovieSearch('');
                      setCategoryFilter('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setSelectedDetailMovie(movie);
                        setMovieSearch('');
                        setCategoryFilter('');
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(movie.id);
                      }
                    }}
                    className={`p-3 rounded-2xl bg-zinc-900/60 border border-zinc-800/80 hover:bg-zinc-900 hover:border-zinc-700 transition flex items-center justify-between gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-theme-accent ${
                      isWatchedMovie ? 'opacity-45 hover:opacity-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-14 rounded-xl bg-black/60 border border-zinc-800 overflow-hidden shrink-0 relative">
                        <img
                          src={getSafeImageUrl(movie.poster)}
                          alt={movie.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=300';
                          }}
                        />
                        {hasWatched && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black">
                            <div
                              className="h-full bg-theme-accent"
                              style={{ width: `${prog.percentage}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <AutoScrollTitle
                            text={movie.title}
                            className="text-xs md:text-sm font-bold text-white"
                          />
                          {isWatchedMovie && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1 shrink-0">
                              <CheckCircle2 className="w-3 h-3" /> Assistido
                            </span>
                          )}
                          {getDisplayYear(movie.title, movie.year) && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 shrink-0">
                              {getDisplayYear(movie.title, movie.year)}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400 truncate mt-0.5">
                          {movie.category} • {movie.description ? movie.description.slice(0, 90) + '...' : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div
                        aria-hidden="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(movie.id);
                        }}
                        className="p-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-amber-400 transition cursor-pointer"
                      >
                        <Star
                          className={`w-4 h-4 ${
                            fav ? 'fill-amber-400 text-amber-400' : ''
                          }`}
                        />
                      </div>

                      <button
                        onClick={() => setSelectedDetailMovie(movie)}
                        className="px-3.5 py-2 rounded-xl bg-theme-accent hover:opacity-90 text-black font-black text-xs flex items-center gap-1.5 transition"
                      >
                        <Play className="w-3.5 h-3.5 fill-current" />
                        <span>Detalhes</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {categoryMovies.length > visibleLimit && (
            <div className="pt-6 pb-6 flex flex-col items-center justify-center gap-2 opacity-80">
              <OpenTvLoader size="compact" glow="none" />
              <span className="text-[11px] text-zinc-400 font-medium">
                Carregando mais filmes... ({displayedMovies.length} de {categoryMovies.length})
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
